/**
 * 文件定位：
 * 1. 当前文件位于 Worker 倒角求解 class 层；
 * 2. 负责 SolidWorks Angle Distance 倒角的 Puppy CAD 内核 构建；
 * 3. 不承担定义归一化和 UI 字段解析。
 *
 * 文件职责：
 * 1. 校验角度距离倒角的距离和角度；
 * 2. 解析选中边及支撑面；
 * 3. 调用 Puppy CAD 内核 AddDA 完成距离-角度倒角。
 */

import {
  resolveEdgeSelections,
} from '../../common';
import { resolveEdgeSupportFace } from '../../edge-support-face-resolution';
import {
  assertChamferBuilderResultSafe,
  buildProgressRange,
  safeDelete,
  toRadians,
} from '../functions/chamferPuppyKernel';
import type { ChamferShapeBuildInput } from '../types/ChamferShapeBuildInput';
import { ChamferSolver } from './ChamferSolver';

/**
 * 角度-距离倒角求解器。
 */
export class AngleDistanceChamferSolver extends ChamferSolver {
  
  public readonly type = 'angle-distance' as const;

  /**
   * @inheritdoc
   */
  public build(input: ChamferShapeBuildInput): unknown {
    const { puppy, sourceShape, sourceMetadata, sourceObjectId, definition } = input;
    if (typeof definition.distance !== 'number' || !Number.isFinite(definition.distance) || definition.distance <= 0) {
      throw new Error('角度-距离倒角缺少有效距离');
    }
    if (
      typeof definition.angleDeg !== 'number'
      || !Number.isFinite(definition.angleDeg)
      || definition.angleDeg < 0
      || definition.angleDeg > 180
    ) {
      throw new Error('角度-距离倒角缺少有效角度');
    }

    const builder = new puppy.BRepFilletAPI_MakeChamfer(sourceShape);
    const selections = resolveEdgeSelections(
      puppy,
      sourceShape,
      sourceMetadata,
      definition.items,
      { sourceObjectId },
    );
    const progress = buildProgressRange(puppy);

    try {
      selections.forEach(({ edgeShape, selection }) => {
        const supportFace = resolveEdgeSupportFace(
          puppy,
          sourceShape,
          sourceMetadata,
          selection,
          edgeShape,
          {
            /**
             * 角度-距离倒角允许围绕另一侧支撑面翻转方向。
             * 方向选择必须在 worker 支撑面解析里完成，不能由 UI 猜相邻面顺序。
             */
            preferredFaceOffset: definition.flipDirection === true ? 1 : 0,
          },
        );
        builder.AddDA(definition.distance as number, toRadians(definition.angleDeg as number), edgeShape, supportFace);
      });
      builder.Build(progress);
      return assertChamferBuilderResultSafe(puppy, builder);
    } finally {
      safeDelete(progress);
      safeDelete(builder);
    }
  }
}
